// Pending CSRF state for the Android Apple sign-in round trip.
//
// The value must survive the browser hand-off and a possible process death,
// so it lives in Capacitor Preferences (localStorage fallback per project
// convention). It is never logged and never sent to analytics.

import { Preferences } from '@capacitor/preferences';

const KEY = 'apple_oauth_pending_state';
const TTL_MS = 10 * 60 * 1000; // 10 minutes

interface PendingState {
  state: string;
  createdAt: number;
}

async function readRaw(): Promise<string | null> {
  try {
    const { value } = await Preferences.get({ key: KEY });
    if (value) return value;
  } catch {
    /* fall through to web storage */
  }
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

async function writeRaw(value: string): Promise<void> {
  try {
    await Preferences.set({ key: KEY, value });
  } catch {
    /* fall through to web storage */
  }
  try {
    localStorage.setItem(KEY, value);
  } catch {
    /* storage unavailable */
  }
}

export async function clearPendingState(): Promise<void> {
  try {
    await Preferences.remove({ key: KEY });
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

export async function putPendingState(state: string): Promise<void> {
  const payload: PendingState = { state, createdAt: Date.now() };
  await writeRaw(JSON.stringify(payload));
}

/** True when a (non-expired) pending state exists. Does not consume it. */
export async function hasPendingState(): Promise<boolean> {
  const raw = await readRaw();
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as PendingState;
    return Boolean(parsed?.state) && Date.now() - parsed.createdAt <= TTL_MS;
  } catch {
    return false;
  }
}

/**
 * Read and delete the pending state. Returns null when nothing is pending,
 * the stored value is unreadable, or it is older than the 10 minute window.
 */
export async function takePendingState(): Promise<string | null> {
  const raw = await readRaw();
  await clearPendingState();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PendingState;
    if (!parsed?.state) return null;
    if (Date.now() - parsed.createdAt > TTL_MS) return null;
    return parsed.state;
  } catch {
    return null;
  }
}

/** Length-safe comparison that does not short-circuit on the first difference. */
export function statesMatch(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b || a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}
